// Pure decisions for the connect flow (/onboarding + /connections) — free of React so they unit-test without a DOM.
import type { ConnectionKind } from '@common-reducers/ConnectedEnginesSlice';

export type OnboardingPath = 'local' | 'remote';
export type ExtensionAccount = 'match' | 'other' | 'signedOut' | 'unknown';

/** Which Blueticks account the extension is signed in to, as far as it can tell us. Older builds
 *  can't report it ('unknown') — treated as fine; the 25s account hint still covers them. */
export function extensionAccount(i: { reportsUser: boolean; extensionUserId: string | null; myUserId: string | null }): ExtensionAccount {
  if (!i.reportsUser || !i.myUserId) return 'unknown';
  if (!i.extensionUserId) return 'signedOut';
  return i.extensionUserId === i.myUserId ? 'match' : 'other';
}

export type ConditionId = 'L1' | 'L2' | 'L3' | 'R1' | 'R2' | 'R3' | 'M1';
export type FlowScreen = 'choose' | 'local' | 'remote' | 'message' | 'done';

export interface FlowInput {
  kind: ConnectionKind;
  path: OnboardingPath | null;
  /** "Choose another way" — the choice wins until they pick again (never over a live connection). */
  chooseAgain: boolean;
  hasExtension: boolean;
  account: ExtensionAccount;
  /** R1 — the plan allows a remote connection (Pro, or a Pro trial). */
  canRemote: boolean;
  /** R2 — a remote connection exists that is not linked yet. */
  pendingRemote: boolean;
  /** M1 — see messageDoneFrom. */
  messageDone: boolean;
}

export interface FlowState {
  screen: FlowScreen;
  /** The path on screen — a live connection's own kind wins over the remembered choice. */
  path: OnboardingPath | null;
  conditions: { id: ConditionId; met: boolean }[];
  /** The first unmet condition — what the screen helps with. Null on the choice and when all are met. */
  current: ConditionId | null;
  progress: { pct: number; step: 1 | 2 | 3; done: boolean };
}

/** The whole wizard in one decision: every condition for a working Blueticks, read from live
 *  state, and the first one not met yet. Coming back after installing, paying in another tab or
 *  connecting elsewhere always lands on the right condition. */
export function conditionsFor(i: FlowInput): FlowState {
  const connected = i.kind !== 'none';
  const path: OnboardingPath | null = connected
    ? (i.kind === 'remote' ? 'remote' : 'local')
    : i.chooseAgain ? null
    : i.pendingRemote && i.path !== 'local' ? 'remote'
    : i.path;
  const local = [
    { id: 'L1' as const, met: connected || i.hasExtension },
    { id: 'L2' as const, met: connected || (i.hasExtension && i.account !== 'other' && i.account !== 'signedOut') },
    { id: 'L3' as const, met: connected },
  ];
  const remote = [
    { id: 'R1' as const, met: connected || i.canRemote },
    { id: 'R2' as const, met: connected || (i.canRemote && i.pendingRemote) },
    { id: 'R3' as const, met: connected },
  ];
  const conditions = [...(path === 'local' ? local : path === 'remote' ? remote : []), { id: 'M1' as const, met: connected && i.messageDone }];
  const done = connected && i.messageDone;
  const met = conditions.filter(c => c.met).length;
  return {
    screen: connected ? (done ? 'done' : 'message') : path ?? 'choose',
    path,
    conditions,
    current: path === null ? null : conditions.find(c => !c.met)?.id ?? null,
    progress: { pct: done ? 100 : Math.max(6, Math.round((met / 4) * 100)), step: done ? 3 : connected ? 2 : 1, done },
  };
}

/** M1 — the first message is out. New accounts get the 4-step first-week checklist and need its
 *  self_test; an established account (any other checklist) has nothing to prove. */
export function messageDoneFrom(checklist: { steps: { id: string; done: boolean }[] } | undefined, deliveredHere: boolean): boolean {
  if (deliveredHere) return true;
  if (!checklist) return false;
  if (checklist.steps.length !== 4) return true;
  return !!checklist.steps.find(s => s.id === 'self_test')?.done;
}

export type LocalSupport = 'chrome' | 'otherBrowser' | 'phone';

/** Can THIS device run the extension ("on this computer")? Decided by the device, never by the
 *  window width — a narrow Chrome window on a laptop is still a computer.
 *  - chrome:       Chrome or another Chromium that installs from the Chrome Web Store (Edge, Brave, Opera)
 *  - otherBrowser: a computer in Safari / Firefox — the computer is fine, the browser is not
 *  - phone:        phones and tablets — incl. the ones asking for the desktop site: an iPad sends a
 *                  Mac UA, an Android tablet (or a phone on "Desktop site") a Linux one; both have touch.
 *                  `platform` (navigator.userAgentData.platform) still says "Android" there. */
export function localSupport(userAgent: string, maxTouchPoints: number, platform?: string): LocalSupport {
  if (/Android|iPhone|iPad|iPod|Mobi/i.test(userAgent) || /Android/i.test(platform ?? '')) return 'phone';
  if (maxTouchPoints > 1 && (/Macintosh/i.test(userAgent) || (/Linux/i.test(userAgent) && !/CrOS/i.test(userAgent)))) return 'phone';
  if (/Firefox|FxiOS/i.test(userAgent)) return 'otherBrowser';
  // Safari's UA has "Safari" but no "Chrome"/"Chromium"/"Edg".
  return /Chrome|Chromium|Edg\//i.test(userAgent) ? 'chrome' : 'otherBrowser';
}

/** The test message is due a minute out — the scheduler rounds to the minute, so the real
 *  wait is 0-60s and the on-screen countdown is the actual one. */
export function firstMessageDueDate(now: Date): Date {
  const d = new Date(now.getTime() + 60_000);
  d.setSeconds(0, 0);
  return d;
}

const SENT = new Set(['sent', 'sent_pending_ack', 'bt-sent', 'confirmed', 'delivered', 'received', 'read', 'played']);
const FAILED = new Set(['error', 'failed', 'expired', 'cancelled']);

export type TrackedStatus = 'scheduled' | 'sent' | 'failed';

export function trackedStatus(status: string | undefined): TrackedStatus {
  if (status && SENT.has(status)) return 'sent';
  if (status && FAILED.has(status)) return 'failed';
  return 'scheduled';
}

/** "1:07" — for the Done screen's time-to-value and the countdown. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** The user's own WhatsApp id from their profile phone — only when it is a full international
 *  number. A hand-typed local number ("050…") is NOT a WhatsApp id; the backend refuses it too,
 *  so we ask for the number instead of messaging a dead address labelled "You". */
export function ownWidFromPhone(phone: string | null | undefined): string | null {
  const raw = String(phone ?? '').trim();
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 10 || (!raw.startsWith('+') && digits.startsWith('0'))) return null;
  return `${digits}@c.us`;
}

/** The user's own WhatsApp id from a CONNECTED always-on connection: the remote engine stores its
 *  own number on the session (session.phoneNumber) the moment it links — the remote twin of the
 *  extension writing the profile phone. Lets onboarding show "You" without waiting on the server. */
export function ownWidFromEngines(engines: ReadonlyArray<{ type: string; status: string; session?: { status?: string; phoneNumber?: string | null } }>): string | null {
  for (const e of engines) {
    if (e.type !== 'gateway' || (e.status !== 'connected' && e.session?.status !== 'connected')) continue;
    const wid = ownWidFromPhone(e.session?.phoneNumber);
    if (wid) return wid;
  }
  return null;
}

/** WhatsApp id for a phone number typed by the user ("+972 50-123 4567" → "972501234567@c.us"). */
export function phoneToWid(phone: string | null | undefined): string | null {
  const digits = String(phone ?? '').replace(/\D/g, '');
  return digits.length >= 7 ? `${digits}@c.us` : null;
}

/** Where a "this computer" connection is stuck — read from the extension's own ping answer.
 *  The account is checked BEFORE the WhatsApp tab: the extension reports it from its background,
 *  so a wrong account is fixed before the user scans anything.
 *  - install:      no extension answered in this browser
 *  - signedOut:    the extension isn't signed in to any Blueticks account
 *  - otherAccount: the extension is signed in to a DIFFERENT account
 *  - openWa:       right account (or an older extension that can't tell), no WhatsApp Web tab
 *  - linking:      WhatsApp Web open — still loading / waiting for its QR scan */
export type LocalSubstate = 'install' | 'openWa' | 'signedOut' | 'otherAccount' | 'linking';

export function localSubstate(input: { hasExtension: boolean; waTabOpen: boolean; account: ExtensionAccount }): LocalSubstate {
  if (!input.hasExtension) return 'install';
  if (input.account === 'signedOut') return 'signedOut';
  if (input.account === 'other') return 'otherAccount';
  return input.waTabOpen ? 'linking' : 'openWa';
}

/** R2's connection: a remote (always-on) gateway that exists but isn't linked yet — the one just
 *  created when known, else the first one. Legacy, connected and update-required ones never count. */
export function pendingGateway<E extends { id: string; type: string; status: string; isLegacy?: boolean }>(engines: E[], createdId: string | null): E | undefined {
  const waiting = engines.filter(e => e.type === 'gateway' && !e.isLegacy && e.status !== 'connected' && e.status !== 'update_required');
  return waiting.find(e => e.id === createdId) ?? waiting[0];
}

/** The calendar opens the wizard by itself exactly once per account: when the live state has
 *  settled, the setup isn't done, and the user has never seen /onboarding. After that, the
 *  sidebar progress is the way back. */
export function shouldAutoOpenOnboarding(i: { ready: boolean; done: boolean; seen: boolean }): boolean {
  return i.ready && !i.done && !i.seen;
}

export type SetupAction = 'choose' | 'installExtension' | 'signInExtension' | 'openWhatsApp' | 'unlockAlwaysOn' | 'createAlwaysOn' | 'scanQr' | 'firstMessage';

const ACTION_FOR: Record<ConditionId, SetupAction> = {
  L1: 'installExtension', L2: 'signInExtension', L3: 'openWhatsApp',
  R1: 'unlockAlwaysOn', R2: 'createAlwaysOn', R3: 'scanQr',
  M1: 'firstMessage',
};

/** The very next thing the user has to do — what the sidebar progress says out loud.
 *  Null once everything is done. */
export function setupNextAction(flow: FlowState): SetupAction | null {
  if (flow.screen === 'choose') return 'choose';
  return flow.current ? ACTION_FOR[flow.current] : null;
}
